/**
 * useSpectatorSocket — WebSocket hook for spectator mode.
 *
 * Cloned from useWorkspaceSocket but uses share_id instead of JWT auth.
 * Connects to /api/v1/workspace/spectate?share_id=<id>
 */
import { useState, useEffect, useCallback, useRef } from 'react'
import type { ToolCallEvent, WorkspaceEvent } from './useWorkspaceSocket'

export type { ToolCallEvent }

export interface ReactionEvent {
  emoji: string
  timestamp: string
}

interface UseSpectatorSocketReturn {
  events: ToolCallEvent[]
  connected: boolean
  connecting: boolean
  error: string | null
  /** Whether the link was created with "include existing progress" */
  includeExisting: boolean
  /** ISO timestamp of when the link was created (for filtering pre-link events) */
  linkCreatedAt: string | null
  /** Number of spectators currently watching */
  spectatorCount: number
  /** Reaction events received */
  reactions: ReactionEvent[]
  /** Whether the session was revoked by the presenter */
  revoked: boolean
  /** Send a reaction emoji */
  sendReaction: (emoji: string) => void
  connect: () => void
  disconnect: () => void
  clearEvents: () => void
}

const MAX_EVENTS = 100

export function useSpectatorSocket(
  shareId: string,
  authToken?: string | null,
  onTokenExpired?: () => Promise<string | null>,
): UseSpectatorSocketReturn {
  const [events, setEvents] = useState<ToolCallEvent[]>([])
  const [connected, setConnected] = useState(false)
  const [connecting, setConnecting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [includeExisting, setIncludeExisting] = useState(true)
  const [linkCreatedAt, setLinkCreatedAt] = useState<string | null>(null)
  const [spectatorCount, setSpectatorCount] = useState(0)
  const [reactions, setReactions] = useState<ReactionEvent[]>([])
  const [revoked, setRevoked] = useState(false)

  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const reconnectAttemptsRef = useRef(0)
  const MAX_RECONNECT_ATTEMPTS = 3
  const pingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const connectedAtRef = useRef<number | null>(null)
  const lastPongRef = useRef<number | null>(null)
  const tokenRef = useRef<string | null | undefined>(authToken)
  const onTokenExpiredRef = useRef(onTokenExpired)
  onTokenExpiredRef.current = onTokenExpired

  // Keep tokenRef in sync with prop
  useEffect(() => {
    tokenRef.current = authToken
  }, [authToken])

  const clearEvents = useCallback(() => {
    setEvents([])
  }, [])

  const sendReaction = useCallback((emoji: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'reaction', emoji }))
    }
  }, [])

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current)
      pingIntervalRef.current = null
    }
    if (wsRef.current) {
      wsRef.current.close()
      wsRef.current = null
    }
    setConnected(false)
    setConnecting(false)
  }, [])

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN || connecting || !shareId) {
      return
    }

    setConnecting(true)
    setError(null)

    // Use production host always (spectator is public)
    const isLocalDev =
      window.location.hostname === 'localhost' || window.location.hostname.startsWith('192.168')
    const wsProtocol = isLocalDev ? 'wss:' : window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const wsHost = isLocalDev ? 'ecosemantic.com' : window.location.host
    let wsUrl = `${wsProtocol}//${wsHost}/api/v1/workspace/spectate?share_id=${shareId}`
    const currentToken = tokenRef.current
    if (currentToken) {
      wsUrl += `&token=${encodeURIComponent(currentToken)}`
    }

    try {
      const ws = new WebSocket(wsUrl)
      wsRef.current = ws

      ws.onopen = () => {
        connectedAtRef.current = Date.now()
        setConnected(true)
        setConnecting(false)
        setError(null)
        reconnectAttemptsRef.current = 0
        console.log('[Spectator] connected')

        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            const sincePong = lastPongRef.current
              ? Math.round((Date.now() - lastPongRef.current) / 1000)
              : 'never'
            console.log(`[Spectator] ping (last pong ${sincePong}s ago)`)
            ws.send('ping')
          }
        }, 30000)
      }

      ws.onmessage = (event) => {
        if (event.data === 'pong') {
          lastPongRef.current = Date.now()
          return
        }

        try {
          const raw = JSON.parse(event.data)
          if (raw.type === 'session_revoked') {
            setRevoked(true)
            ws.close()
            return
          }
          const data = raw as WorkspaceEvent

          if (data.type === 'tool_call') {
            const toolEvent = data as ToolCallEvent
            setEvents((prev) => {
              if (toolEvent.call_id && toolEvent.data.status !== 'started') {
                const existingIndex = prev.findIndex(
                  (e) => e.call_id === toolEvent.call_id && e.data.status === 'started',
                )
                if (existingIndex !== -1) {
                  const without = prev.filter((_, i) => i !== existingIndex)
                  return [toolEvent, ...without].slice(0, MAX_EVENTS)
                }
              }
              return [toolEvent, ...prev].slice(0, MAX_EVENTS)
            })
          } else if (data.type === 'connected') {
            // Extract link settings from connection message
            const connData = data as unknown as Record<string, unknown>
            if (connData.include_existing !== undefined) {
              setIncludeExisting(!!connData.include_existing)
            }
            if (connData.link_created_at) {
              setLinkCreatedAt(connData.link_created_at as string)
            }
            if (typeof connData.spectator_count === 'number') {
              setSpectatorCount(connData.spectator_count)
            }
          } else if (data.type === 'spectator_count') {
            const countData = data as unknown as Record<string, unknown>
            // Per-project counts include share_id; session-wide counts have null.
            // Only accept counts scoped to this link or unscoped (session-wide).
            const eventShareId = countData.share_id as string | null
            if (
              typeof countData.count === 'number' &&
              (!eventShareId || eventShareId === shareId)
            ) {
              setSpectatorCount(countData.count)
            }
          } else if (data.type === 'reaction') {
            const rData = data as unknown as Record<string, unknown>
            if (rData.emoji) {
              setReactions((prev) =>
                [
                  {
                    emoji: rData.emoji as string,
                    timestamp: (rData.timestamp as string) || new Date().toISOString(),
                  },
                  ...prev,
                ].slice(0, 50),
              ) // keep last 50
            }
          } else if (data.type === 'error') {
            setError(data.message)
          }
        } catch {
          // Ignore parse errors
        }
      }

      ws.onclose = (ev) => {
        const uptime = connectedAtRef.current
          ? Math.round((Date.now() - connectedAtRef.current) / 1000)
          : '?'
        const sincePong = lastPongRef.current
          ? Math.round((Date.now() - lastPongRef.current) / 1000)
          : 'never'
        console.warn(
          `[Spectator] closed code=${ev.code} reason="${ev.reason}" clean=${ev.wasClean} uptime=${uptime}s lastPong=${sincePong}s ago`,
        )
        connectedAtRef.current = null
        setConnected(false)
        setConnecting(false)

        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current)
          pingIntervalRef.current = null
        }

        if (ev.code === 4003 && onTokenExpiredRef.current) {
          console.log('[Spectator] 4003 received — attempting token refresh')
          onTokenExpiredRef
            .current()
            .then((newToken) => {
              if (newToken) {
                tokenRef.current = newToken
                reconnectAttemptsRef.current = 0
                console.log('[SpectatorSocket] Token refreshed, reconnecting...')
                reconnectTimeoutRef.current = setTimeout(() => {
                  connect()
                }, 500)
              } else {
                setError('Session expired — please log in again')
              }
            })
            .catch(() => {
              setError('Session expired — please log in again')
            })
        } else if (ev.code === 4004) {
          setError('Spectator link expired or invalid')
        } else if (ev.code !== 1000) {
          // Other unexpected close — standard backoff reconnect
          reconnectAttemptsRef.current++
          if (reconnectAttemptsRef.current <= MAX_RECONNECT_ATTEMPTS) {
            reconnectTimeoutRef.current = setTimeout(() => {
              connect()
            }, 5000)
          } else {
            setError('Unable to connect — session may have ended')
          }
        }
      }

      ws.onerror = () => {
        setError('WebSocket connection error')
        setConnecting(false)
      }
    } catch {
      setError('Failed to create WebSocket connection')
      setConnecting(false)
    }
  }, [connecting, shareId])

  useEffect(() => {
    return () => {
      disconnect()
    }
  }, [disconnect])

  return {
    events,
    connected,
    connecting,
    error,
    includeExisting,
    linkCreatedAt,
    spectatorCount,
    reactions,
    revoked,
    sendReaction,
    connect,
    disconnect,
    clearEvents,
  }
}
